import React, { useCallback, useState } from 'react';
import { useTemplateOrCalculation } from '../../../../hooks/useTemplateOrCalculation';
import { ConfigurationSwitch, RowCentered } from '@ydistri/ds';
import { rtkQueryPatchStatusToConfigurationFieldStatus } from '@ydistri/utils';
import {
  DeleteBrandConfigurationPayload,
  UpdateBrandConfigurationPayload,
  useDeleteBrandConfigurationMutation,
  useUpdateBrandConfigurationMutation,
} from '../apiBrands';
import { InfoIconTooltip } from '../../../../components/icons/InfoIconTooltip';

interface BrandConfigurationSwitchProps {
  disabled?: boolean;
  configured: boolean;
  brandId: number;
  calculationBrandConfiguration: boolean;
}

type LastConfigurationStatusChange = 'updateConfigurationStatus' | 'deleteConfigurationStatus';

const BrandConfigurationSwitch: React.FC<BrandConfigurationSwitchProps> = ({
  disabled = false,
  configured,
  brandId,
  calculationBrandConfiguration,
}) => {
  const templateOrCalculation = useTemplateOrCalculation();
  const [updateConfiguration, updateConfigurationStatus] = useUpdateBrandConfigurationMutation();
  const [deleteConfiguration, deleteConfigurationStatus] = useDeleteBrandConfigurationMutation();

  const [latestAction, setLatestAction] = useState<LastConfigurationStatusChange>(
    'updateConfigurationStatus',
  );
  const latestStatus =
    latestAction === 'updateConfigurationStatus'
      ? updateConfigurationStatus
      : deleteConfigurationStatus;
  const status = rtkQueryPatchStatusToConfigurationFieldStatus(latestStatus);

  const updateBrand = useCallback(() => {
    if (!configured) {
      const updatePayload: UpdateBrandConfigurationPayload = {
        templateOrCalculation,
        brandId,
      };
      setLatestAction('updateConfigurationStatus');
      updateConfiguration(updatePayload);
    } else {
      const deletePayload: DeleteBrandConfigurationPayload = {
        templateOrCalculation,
        brandId,
      };
      setLatestAction('deleteConfigurationStatus');
      deleteConfiguration(deletePayload);
    }
  }, [configured, templateOrCalculation, brandId, updateConfiguration, deleteConfiguration]);

  return (
    <RowCentered $gap="1rem">
      <ConfigurationSwitch
        disabled={disabled}
        status={status}
        value={configured}
        onChange={updateBrand}
        includeEmptySpace={false}
        switchColor={calculationBrandConfiguration ? 'red' : undefined}
      />
      {configured && (
        <InfoIconTooltip
          content={
            calculationBrandConfiguration
              ? 'Brand is disabled - products from this brand will not be used in redistribution.'
              : 'Brand is enabled - products from this brand will be used in redistribution.'
          }
        />
      )}
    </RowCentered>
  );
};

export default BrandConfigurationSwitch;
